import React, { useState, useEffect } from 'react';
import { Search, Home } from 'lucide-react';
import EmployeeAcknowledgment from './EmployeeAcknowledgment';
import DocumentArchiveButton from './DocumentArchiveButton';
import PVSPageLayout from './PVSPageLayout';
import CalorVesselsNotice from './documents/CalorVesselsNotice';

const WSEDocumentViewer = ({ onReturnHome }) => {
  const [content, setContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/wse-content.md');
        if (!response.ok) throw new Error('Failed to load WSE content');
        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError('Failed to load the document. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const highlightSearchTerm = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-pvs-accent/30 text-white">$1</mark>');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pvs-accent"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-md">
          <p className="text-red-400">{error}</p>
        </div>
      );
    }

    // Split content into sections based on headers
    const sections = content.split(/(?=# |\n## )/g);
    
    return sections.map((section, index) => {
      const htmlContent = highlightSearchTerm(section);
      return (
        <div key={index} className="mb-8">
          <div 
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: htmlContent
                .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold text-pvs-accent mb-6">$1</h1>')
                .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold text-pvs-accent mb-4">$1</h2>')
                .replace(/^### (.*$)/gm, '<h3 class="text-xl font-bold text-pvs-accent mb-3">$1</h3>')
                .replace(/^- (.*$)/gm, '<li class="ml-4 text-gray-300">$1</li>')
            }}
          />
        </div>
      );
    });
  };

  return (
    <PVSPageLayout className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass w-full">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-6 gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">Written Scheme of Examination</h1>
              <DocumentArchiveButton />
              <button
                onClick={onReturnHome}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover"
                title="Return to Homepage"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-pvs-text-dim" />
              <input
                type="search"
                placeholder="Search document..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pvs-input w-full pl-10"
              />
            </div>
          </div>
          
          <div className="p-6 text-gray-300">
            <CalorVesselsNotice />
            {renderContent()}
            
            {/* Employee Acknowledgment Section */}
            <EmployeeAcknowledgment 
              documentName="Written Scheme of Examination"
              documentReference=""
              processActivity="Vessel Examination and Testing"
              versionNumber=""
              dateOfIssue=""
            />
          </div>
        </div>
      </div>
    </PVSPageLayout>
  );
};

export default WSEDocumentViewer;
